import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import { resignationApi } from "./resignation";

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    previewTerminationOrder: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/hr/resignation-letters/${id}/order-preview/`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),

    signTerminationOrder: builder.mutation<SigningActionResponse, { id: number; resignationId: number; cms_signature: string }>({
      query: ({ id, cms_signature }) => ({
        url: `/api/hr/termination-orders/${id}/sign/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include",
      }),
      invalidatesTags: () => [
        { type: "ResignationLetters" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted({ resignationId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                item.action_required === "sign_document" &&
                "document_id" in item.details &&
                (item.details as SignDocumentDetails).source_type === "resignation_letter",
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(
            resignationApi.util.updateQueryData("getResignationLetter", resignationId, (draft) => {
              draft.status = "order_uploaded";
            }),
          );
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
  }),
});

export const {
  usePreviewTerminationOrderMutation,
  useSignTerminationOrderMutation,
} = ordersApi;
